import type { SanityHeroForm } from '#/lib/sanity/queries/heroForm';
import {
  getSectionButtonTheme,
  getSectionThemeAccent,
  getSectionThemeSurface,
  isDarkSectionTheme,
  normalizeSectionTheme,
} from '#/lib/section-theme';
import { cn, COLORS } from '#/lib/utils';

import { FormCustom } from './Form/Custom';
import { Headline } from './Headline';
import {
  getHeroBackground,
  HeroBackgroundGraphic,
} from './HeroBackgroundGraphic';
import { LogoMarquee } from './LogoMarquee';
import { Media } from './Media';
import { RichText } from './RichText';
import { SocialProof } from './SocialProof/SocialProof';

/**
 * Form theming notes:
 * - white → light inputs (current default / fallback for unthemed modules)
 * - charcoal → dark inputs (same treatment as Hero Home)
 * - purple → light inputs on the purple surface. Dark inputs are tuned for
 *   charcoal (grayscale-06), not cipher, so purple keeps the light form as a
 *   safe fallback until dedicated purple form styles exist.
 */
export function HeroForm(props: SanityHeroForm) {
  const isFirst = props.sectionIndex === 0;
  // Unthemed legacy modules keep today's cloud look.
  const theme = normalizeSectionTheme(props.theme, 'white');
  const isDark = isDarkSectionTheme(theme);
  const accentClass = getSectionThemeAccent(theme);
  // Only charcoal gets dark form controls; purple stays light (see note above).
  const formTheme = theme === 'charcoal' ? 'dark' : 'light';
  const background = getHeroBackground(theme, props.backgroundPattern);
  // Edge fades on LogoMarquee are built from `background` (solid → transparent).
  // Match the painted surface so the fade doesn't disappear on dark themes.
  const marqueeBackground =
    background?.baseColor ??
    (theme === 'purple'
      ? COLORS.cipher.DEFAULT
      : theme === 'charcoal'
        ? COLORS.charcoal
        : COLORS.cloud);

  return (
    <div
      className={cn(
        'p-main',
        getSectionThemeSurface(theme),
        background && 'relative isolate overflow-hidden',
        isFirst
          ? 'mt-[calc(var(--header-height)*-1)] pb-100 pt-[calc(var(--header-height)+5rem)]'
          : 'py-100',
      )}
      style={background ? { backgroundColor: background.baseColor } : undefined}
    >
      {background ? <HeroBackgroundGraphic background={background} /> : null}
      <div className="fade-in grid-main mx-auto max-w-full l:max-w-1500">
        <div className="col-span-full l:col-span-6">
          <div className="max-w-600 l:max-w-490 l:mx-auto h-full flex flex-col">
            <Headline
              value={props.headline}
              highlightClass={accentClass}
              className="mb-25 text-32 m:text-43 l:text-43"
            />
            <RichText
              value={props.bodyCopy ?? []}
              normalTextSizeClassName={cn(
                'text-body',
                isDark ? 'text-cloud' : 'text-charcoal',
              )}
            />
            <SocialProof socialProof={props.socialProof} />
            <div className="flex-1 mt-20">
              <FormCustom
                className={cn(
                  'flex h-full flex-col',
                  // Privacy + step label must stay readable on purple/charcoal
                  // even when inputs stay light.
                  isDark &&
                    '[&_.legal-text]:!text-cloud [&_.step-indicator]:!text-cloud',
                )}
                form={props.form}
                theme={formTheme}
                // Purple keeps light inputs; CTA still follows the dark surface.
                buttonTheme={getSectionButtonTheme(theme)}
                confirmation={{
                  ...props.formConfirmation,
                  headline: undefined,
                }}
              />
            </div>
          </div>
        </div>
        <div className="hidden l:block col-span-6 l:col-start-7">
          <Media media={props.formMedia} sizes="50vw" />
        </div>
      </div>
      {props.logoMarquee && (
        <LogoMarquee
          {...props.logoMarquee}
          background={marqueeBackground}
          foreground={isDark ? COLORS.cloud : COLORS.charcoal}
          className="mt-60"
        />
      )}
    </div>
  );
}
